import { Close, Public } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GeoJSON, MapContainer, useMap, useMapEvents, ZoomControl } from 'react-leaflet';

import {
  oilProvincesApi,
  type OilProvinceDetail,
  type OilProvinceGeoJSON,
} from '@/api/oilProvinces';
import { MENA_DEFAULT_ZOOM, MENA_MAP_CENTER } from '@/constants/fieldMap';
import { filterVisibleLayerAttributes } from '@/data/hiddenLayerAttributes';

import { MapBaseLayers } from './mapLayers';
import { ProvinceTemplateCreateModal } from './ProvinceTemplateCreateModal';

/** Coarser = larger simplify tolerance (fewer vertices). */
const DETAIL_LEVELS = [
  { minZoom: 0, simplify: 0.12 },
  { minZoom: 4, simplify: 0.05 },
  { minZoom: 7, simplify: 0.02 },
  { minZoom: 9, simplify: 0.01 },
] as const;

const PROVINCE_STYLE: L.PathOptions = {
  color: '#0f4c81',
  weight: 1,
  opacity: 0.85,
  fillColor: '#2b6cb0',
  fillOpacity: 0.28,
};

const PROVINCE_HOVER_STYLE: L.PathOptions = {
  ...PROVINCE_STYLE,
  weight: 2,
  fillOpacity: 0.45,
  fillColor: '#1a567a',
};

function simplifyForZoom(zoom: number): number {
  let simplify = DETAIL_LEVELS[0].simplify;
  for (const level of DETAIL_LEVELS) {
    if (zoom >= level.minZoom) {
      simplify = level.simplify;
    }
  }
  return simplify;
}

function MapFitOnce({ data }: { data: OilProvinceGeoJSON | null }) {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    if (fittedRef.current || !data || data.features.length === 0) {
      return;
    }
    const layer = L.geoJSON(data as any);
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 5 });
      fittedRef.current = true;
    }
  }, [map, data]);

  return null;
}

function ZoomDetailController({
  onZoomSimplify,
}: {
  onZoomSimplify: (simplify: number) => void;
}) {
  const map = useMap();

  useMapEvents({
    zoomend: () => {
      onZoomSimplify(simplifyForZoom(map.getZoom()));
    },
  });

  useEffect(() => {
    onZoomSimplify(simplifyForZoom(map.getZoom()));
  }, [map, onZoomSimplify]);

  return null;
}

interface ProvincesMapViewProps {
  mapHeight?: number | string;
}

export const ProvincesMapView: React.FC<ProvincesMapViewProps> = ({
  mapHeight = 560,
}) => {
  const [mapReady, setMapReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geojson, setGeojson] = useState<OilProvinceGeoJSON | null>(null);
  const [activeSimplify, setActiveSimplify] = useState(DETAIL_LEVELS[0].simplify);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [detail, setDetail] = useState<OilProvinceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  /** Best (smallest) simplify already loaded — never downgrade geometry. */
  const bestSimplifyRef = useRef<number | null>(null);
  const cacheRef = useRef<Map<number, OilProvinceGeoJSON>>(new Map());
  const loadingSimplifyRef = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setMapReady(true), 50);
    return () => {
      window.clearTimeout(timer);
      setMapReady(false);
    };
  }, []);

  const ensureGeometry = useCallback(async (simplify: number, isInitial: boolean) => {
    const cached = cacheRef.current.get(simplify);
    if (cached) {
      if (bestSimplifyRef.current === null || simplify < bestSimplifyRef.current) {
        bestSimplifyRef.current = simplify;
        setActiveSimplify(simplify);
        setGeojson(cached);
      }
      return;
    }

    if (loadingSimplifyRef.current === simplify) {
      return;
    }
    if (bestSimplifyRef.current !== null && simplify >= bestSimplifyRef.current) {
      return;
    }

    loadingSimplifyRef.current = simplify;
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setUpgrading(true);
      }
      setError(null);
      const data = await oilProvincesApi.getGeoJSON(simplify);
      cacheRef.current.set(simplify, data);

      if (bestSimplifyRef.current === null || simplify < bestSimplifyRef.current) {
        bestSimplifyRef.current = simplify;
        setActiveSimplify(simplify);
        setGeojson(data);
      }
    } catch (err) {
      console.error('Failed to load oil provinces:', err);
      if (isInitial) {
        setError('Failed to load oil provinces');
        setGeojson(null);
      }
    } finally {
      if (loadingSimplifyRef.current === simplify) {
        loadingSimplifyRef.current = null;
      }
      setLoading(false);
      setUpgrading(false);
    }
  }, []);

  const handleZoomSimplify = useCallback(
    (simplify: number) => {
      void ensureGeometry(simplify, bestSimplifyRef.current === null);
    },
    [ensureGeometry],
  );

  useEffect(() => {
    void ensureGeometry(DETAIL_LEVELS[0].simplify, true);
  }, [ensureGeometry]);

  useEffect(() => {
    if (!panelOpen || !selectedId) {
      return;
    }

    let cancelled = false;
    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        const data = await oilProvincesApi.getProvince(selectedId);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        console.error('Failed to load province detail:', err);
        if (!cancelled) {
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [panelOpen, selectedId]);

  const handleProvinceClick = useCallback((id: string, name: string) => {
    setSelectedId(id);
    setSelectedName(name);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
    setDetail(null);
    setCreateModalOpen(false);
  }, []);

  const onEachFeature = useCallback(
    (feature: any, layer: L.Layer) => {
      const props = (feature?.properties || {}) as { id?: string; name?: string };
      const name = props.name || 'Province';
      const id = props.id;
      layer.bindTooltip(name, { sticky: true, direction: 'top', opacity: 0.9 });

      layer.on({
        mouseover: (e) => {
          const target = e.target as L.Path;
          target.setStyle(PROVINCE_HOVER_STYLE);
          if (typeof target.bringToFront === 'function') {
            target.bringToFront();
          }
        },
        mouseout: (e) => {
          const target = e.target as L.Path;
          target.setStyle(PROVINCE_STYLE);
        },
        click: () => {
          if (id) {
            handleProvinceClick(id, name);
          }
        },
      });
    },
    [handleProvinceClick],
  );

  const featureCount = geojson?.features.length ?? 0;

  const formatRange = (min: number, max: number) => {
    const fmt = (v: number) =>
      Number.isInteger(v) ? String(v) : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
    return `${fmt(min)} – ${fmt(max)}`;
  };

  const mapCenter = useMemo<[number, number]>(
    () => [MENA_MAP_CENTER.lat, MENA_MAP_CENTER.lng],
    [],
  );

  const canCreateTemplate = Boolean(selectedId && selectedName && detail && !detailLoading);

  const visibleRanges = useMemo(
    () =>
      detail
        ? filterVisibleLayerAttributes(
            detail.ranges,
            (rng) => rng.attribute_id,
            (rng) => rng.attribute_name,
          )
        : [],
    [detail],
  );

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading province contours...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && featureCount === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No oil provinces are available yet.
        </Alert>
      )}

      <Box
        sx={{
          position: 'relative',
          height: mapHeight,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          '& .leaflet-container': { height: '100%', width: '100%', zIndex: 0 },
          '& .leaflet-control-layers': { fontSize: '12px' },
        }}
      >
        {mapReady ? (
          <MapContainer
            center={mapCenter}
            zoom={MENA_DEFAULT_ZOOM}
            minZoom={2}
            maxZoom={12}
            scrollWheelZoom
            preferCanvas
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <MapBaseLayers />
            <ZoomControl position="bottomright" />
            <ZoomDetailController onZoomSimplify={handleZoomSimplify} />
            <MapFitOnce data={geojson} />
            {geojson && (
              <GeoJSON
                key={`provinces-${activeSimplify}-${featureCount}`}
                data={geojson as any}
                style={() => PROVINCE_STYLE}
                onEachFeature={onEachFeature}
              />
            )}
          </MapContainer>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Loading map...
            </Typography>
          </Box>
        )}

        {upgrading && (
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              top: 16,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              bgcolor: 'background.paper',
              boxShadow: 2,
            }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Refining contours…
            </Typography>
          </Box>
        )}

        {panelOpen && selectedId && (
          <Card
            elevation={4}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 1000,
              width: { xs: 'calc(100% - 32px)', sm: 380 },
              maxWidth: 420,
              maxHeight: 'calc(100% - 32px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                pb: '12px !important',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Public color="primary" sx={{ mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {selectedName || detail?.name || 'Province'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Petrophysical attribute ranges
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Close"
                  onClick={handleClosePanel}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {detailLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading ranges...
                  </Typography>
                </Box>
              )}

              {!detailLoading && detail && visibleRanges.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No attribute ranges for this province.
                </Typography>
              )}

              {!detailLoading && detail && visibleRanges.length > 0 && (
                <TableContainer sx={{ maxHeight: 300, mt: 0.5 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Attribute</TableCell>
                        <TableCell align="right">Range</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRanges.map((rng) => (
                        <TableRow key={rng.attribute_id}>
                          <TableCell sx={{ pr: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {rng.attribute_name}
                            </Typography>
                            {rng.unit && (
                              <Typography variant="caption" color="text.secondary">
                                {rng.unit}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatRange(rng.min_value, rng.max_value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!detailLoading && selectedId && selectedName && (
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{ mt: 1.5 }}
                  disabled={!canCreateTemplate}
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create template
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {!loading && featureCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {featureCount} oil province{featureCount === 1 ? '' : 's'} on map.
          {' '}Contours refine as you zoom in. Click a province for attribute ranges.
        </Typography>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Geologic Provinces of the World, 2000 World Petroleum Assessment. U.S. Geological Survey (USGS).
          Public Domain.{' '}
          <Link
            href="https://doi.org/10.5066/P9RMSU2R"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            DOI: 10.5066/P9RMSU2R
          </Link>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Note: petrophysical attribute ranges are estimates.
        </Typography>
      </Box>

      {selectedId && selectedName && detail && (
        <ProvinceTemplateCreateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          provinceId={selectedId}
          provinceName={selectedName}
          ranges={visibleRanges}
        />
      )}
    </Box>
  );
};
